/* ==============================================================
   SleepHappy Variant Cards v3.0.1 — Cache bust + Bulletproof
   ==============================================================
   v3.0.1: Force cache invalidation (file content change → new hash)
   ==============================================================
   v3.0: Fixes price inconsistency caused by 3rd-party apps
         (ecomrise-upsell-bundle, volume_discounts) that overwrite
         label content in Halo's default money format.
         Solution:
         - Per-picker MutationObserver → re-inject if anyone changes
         - Higher specificity selectors
         - Standardized format ฿X,XXX (premium, prefix, no decimals)
         - Force re-apply on a 250ms heartbeat for first 5s after load
         - Debug log: window.__shVCDebug = true
   ============================================================== */
(function () {
  'use strict';

  /* === Format: ฿X,XXX (premium e-commerce style — Casper, Brooklinen) === */
  function formatMoney(cents) {
    if (cents == null) return '';
    var amount = cents / 100;
    return '฿' + amount.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  function getVariantsFromPicker(picker) {
    var script = picker.querySelector('script[data-variants-json]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  /* Fallback: fetch product data via /products/HANDLE.js */
  var variantsCache = {};
  function fetchVariants(handle) {
    if (!handle) return Promise.resolve(null);
    if (variantsCache[handle]) return Promise.resolve(variantsCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.variants) return null;
        variantsCache[handle] = data.variants;
        return data.variants;
      })
      .catch(function () { return null; });
  }

  function getProductHandle(picker) {
    var h = picker.getAttribute('handle');
    if (h) return h;
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function findVariantForOption(variants, optionIndex, value, currentSelections) {
    var matches = variants.filter(function (v) {
      return v['option' + (optionIndex + 1)] === value;
    });
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];

    var narrowed = matches.filter(function (v) {
      for (var i = 0; i < currentSelections.length; i++) {
        if (i === optionIndex) continue;
        if (currentSelections[i] && v['option' + (i + 1)] !== currentSelections[i]) return false;
      }
      return true;
    });
    if (narrowed.length > 0) return narrowed.find(function(v){ return v.available; }) || narrowed[0];
    return matches.find(function(v){ return v.available; }) || matches[0];
  }

  function getCurrentSelections(picker) {
    var sels = [];
    var fieldsets = picker.querySelectorAll('fieldset.hdt-product-form__input');
    fieldsets.forEach(function (fs) {
      var idx = parseInt(fs.dataset.index || '0', 10);
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) {
        sels[idx] = checked.value;
      } else {
        var hidden = fs.querySelector('input[is-value][type="hidden"]');
        if (hidden) sels[idx] = hidden.value;
      }
    });
    return sels;
  }

  /* Build the inner HTML for a label (consistent format guaranteed) */
  function buildLabelHTML(originalText, match) {
    var priceFmt = formatMoney(match.price);
    var compareFmt = (match.compare_at_price && match.compare_at_price > match.price)
      ? formatMoney(match.compare_at_price)
      : null;
    var saleClass = compareFmt ? ' hdt-product-form_value__price--sale' : '';

    var html = '<span class="hdt-product-form_value__name">' + escapeHtml(originalText) + '</span>';
    html += '<div class="hdt-product-form_value__prices">';
    html += '<span class="hdt-product-form_value__price' + saleClass + '" data-sh-price>' + escapeHtml(priceFmt) + '</span>';
    if (compareFmt) {
      html += '<span class="hdt-product-form_value__compare" data-sh-compare>' + escapeHtml(compareFmt) + '</span>';
    }
    html += '</div>';
    return html;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Detect if label needs re-injection.
     Returns true if: not enhanced yet, OR enhanced but price text doesn't
     match expected ฿X,XXX format (3rd party app overwrote with their format). */
  function needsInjection(lab, expectedPrice) {
    if (lab.dataset.shEnhanced !== '1') return true;
    var priceEl = lab.querySelector('[data-sh-price]');
    if (!priceEl) return true;
    var actual = (priceEl.textContent || '').trim();
    /* If text doesn't START with ฿ → some app overwrote it */
    if (actual.indexOf('฿') !== 0) return true;
    /* Check if matches expected exact value (defensive) */
    if (expectedPrice && actual !== expectedPrice) return true;
    return false;
  }

  function doInjection(picker, variants, opts) {
    opts = opts || {};
    var force = opts.force === true;
    var selections = getCurrentSelections(picker);
    var fieldsets = picker.querySelectorAll('fieldset.hdt-product-form__input');

    fieldsets.forEach(function (fs) {
      if (fs.classList.contains('is-style-color')) return;
      var optionIdx = parseInt(fs.dataset.index || '0', 10);

      var labels = fs.querySelectorAll('label.hdt-product-form_value');
      labels.forEach(function (lab) {
        var inp = lab.previousElementSibling;
        if (!inp || inp.type !== 'radio') return;
        var optionValue = inp.value;
        var match = findVariantForOption(variants, optionIdx, optionValue, selections);
        if (!match) return;

        var expectedPriceFmt = formatMoney(match.price);

        /* Skip if already injected correctly AND not forced */
        if (!force && !needsInjection(lab, expectedPriceFmt)) return;

        /* Get original text — if not enhanced yet, take from label;
           if enhanced (might have prices), use saved original */
        var originalText;
        if (lab.dataset.shOriginalText) {
          originalText = lab.dataset.shOriginalText;
        } else {
          originalText = lab.textContent.trim();
          /* Strip any price text that 3rd party apps may have appended */
          originalText = originalText.replace(/[฿$][\d,.\s]+/g, '').trim();
          originalText = originalText.replace(/[\d,.\s]+[฿$].*$/g, '').trim();
        }
        if (!originalText) return;

        if (window.__shVCDebug) {
          console.log('[SH VC] inject', { lab: lab, originalText: originalText, price: expectedPriceFmt });
        }

        lab.innerHTML = buildLabelHTML(originalText, match);
        lab.dataset.shEnhanced = '1';
        lab.dataset.shOriginalText = originalText;
      });
    });

    picker.dataset.shCards = '1';

    /* Attach observer ONCE per picker to re-inject if 3rd party touches labels */
    if (!picker.__shObserved) {
      picker.__shObserved = true;
      var pickerObs = new MutationObserver(function (muts) {
        var needsRefresh = false;
        muts.forEach(function (m) {
          var t = m.target;
          if (!t || !t.closest) return;
          var lab = t.closest && t.closest('label.hdt-product-form_value');
          if (!lab) return;
          /* Skip if mutation was caused by us (within enhanced state) */
          var priceEl = lab.querySelector('[data-sh-price]');
          if (!priceEl || (priceEl.textContent || '').indexOf('฿') !== 0) {
            needsRefresh = true;
          }
        });
        if (needsRefresh) {
          if (window.__shVCDebug) console.log('[SH VC] external change detected, re-injecting');
          /* Use cached variants */
          var cv = getVariantsFromPicker(picker);
          if (!cv) {
            var h = getProductHandle(picker);
            cv = h && variantsCache[h];
          }
          if (cv) doInjection(picker, cv, { force: true });
        }
      });
      pickerObs.observe(picker, { childList: true, subtree: true, characterData: true });
    }
  }

  function injectPricesIntoPicker(picker, opts) {
    if (picker.dataset.shCardsLoading === '1') return;

    var variants = getVariantsFromPicker(picker);
    if (variants) {
      doInjection(picker, variants, opts);
      return;
    }

    picker.dataset.shCardsLoading = '1';
    var handle = getProductHandle(picker);
    if (!handle) { picker.dataset.shCardsLoading = ''; return; }

    fetchVariants(handle).then(function (variants) {
      picker.dataset.shCardsLoading = '';
      if (variants) doInjection(picker, variants, opts);
    });
  }

  function refreshAllPickers(opts) {
    document.querySelectorAll('hdt-variant-picker').forEach(function (p) {
      injectPricesIntoPicker(p, opts);
    });
  }

  function setupChangeListener() {
    document.addEventListener('change', function (e) {
      var picker = e.target.closest && e.target.closest('hdt-variant-picker');
      if (!picker) return;
      setTimeout(function () { injectPricesIntoPicker(picker, { force: true }); }, 60);
    }, true);
  }

  function setupObserver() {
    var observer = new MutationObserver(function (mutations) {
      var hasNewPicker = false;
      mutations.forEach(function (m) {
        m.addedNodes && m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'HDT-VARIANT-PICKER' || (n.querySelector && n.querySelector('hdt-variant-picker'))) {
            hasNewPicker = true;
          }
        });
      });
      if (hasNewPicker) {
        setTimeout(refreshAllPickers, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* Heartbeat: re-check pickers periodically for first 6s in case 3rd party apps
     run after our initial inject. After 6s, rely on per-picker MutationObserver. */
  function setupHeartbeat() {
    var ticks = 0;
    var maxTicks = 24; /* 24 × 250ms = 6s */
    var iv = setInterval(function () {
      ticks++;
      refreshAllPickers();
      if (ticks >= maxTicks) clearInterval(iv);
    }, 250);
  }

  function init() {
    refreshAllPickers();
    setupChangeListener();
    setupObserver();
    setupHeartbeat();
    /* Extra retry for slow-loading pickers (quick view modals etc) */
    setTimeout(refreshAllPickers, 500);
    setTimeout(refreshAllPickers, 1500);
    setTimeout(refreshAllPickers, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
